import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff, User } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Register = () => {
  const { register: registerCtx } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: ''
  });

  const [validation, setValidation] = useState({
    name: true,
    email: true,
    password: true,
    confirmPassword: true
  });

  const checkPasswordStrength = (password) => {
    if (password.length === 0) {
      setPasswordStrength('');
      return;
    }
    if (password.length < 6) {
      setPasswordStrength('weak');
      return;
    }
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 1) setPasswordStrength('weak');
    else if (strength === 2) setPasswordStrength('medium');
    else setPasswordStrength('strong');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setValidation(prev => ({ ...prev, email: emailRegex.test(value) || value === '' }));
    }

    if (name === 'password') {
      checkPasswordStrength(value);
      setValidation(prev => ({ ...prev, password: value.length >= 6 || value === '' }));
    }

    if (name === 'confirmPassword') {
      setValidation(prev => ({ ...prev, confirmPassword: value === formData.password || value === '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = t('common.required');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = t('auth.enter_email');
    }

    if (formData.password.length < 6) {
      errors.password = t('auth.password_requirements');
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('auth.passwords_no_match');
    }

    if (Object.keys(errors).length > 0) {
      setError(Object.values(errors)[0]);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const registerPayload = {
        first_name: formData.name.split(' ').slice(0, -1).join(' ') || formData.name,
        last_name: formData.name.split(' ').slice(-1).join(' ') || '-',
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null
      };

      const result = await registerCtx(registerPayload);

      if (!result.success) {
        throw new Error(result.error || 'Registration failed');
      }

      // await axios.post(`${BACKEND_URL}/api/auth/register`, {
        // name: formData.name,
        // email: formData.email,
        // password: formData.password,
        // phone: formData.phone || null,
        // address: formData.address || null
      // });

      toast({
        title: t('auth.account_created'),
        description: t('auth.account_created_desc'),
      });

      setTimeout(() => {
        navigate('/my-account');
      }, 1500);

    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('common.error');
      setError(errorMessage);
      
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <User className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.create_account')}</h1>
          <p className="text-gray-600">{t('auth.create_account_subtitle')}</p>
        </div>

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('auth.sign_up')}</CardTitle>
            <CardDescription>
              {t('home.find_parts')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.full_name')} *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('auth.enter_name')}
                  required
                  className={!validation.name && formData.name ? 'border-red-500' : ''}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email_address')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('auth.enter_email')}
                  required
                  className={!validation.email && formData.email ? 'border-red-500' : ''}
                  dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                />
                {!validation.email && formData.email && (
                  <p className="text-xs text-red-500">{t('common.error')}</p>
                )}
              </div>

              {/* Phone (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="phone">{t('auth.phone_number')} ({t('auth.optional')})</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('auth.enter_phone')}
                  dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')} *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('auth.enter_password')}
                    required
                    className={!validation.password && formData.password ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      <div className={`h-1 w-1/3 rounded ${passwordStrength ? getPasswordStrengthColor() : 'bg-gray-300'}`} />
                      <div className={`h-1 w-1/3 rounded ${passwordStrength === 'medium' || passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300'}`} />
                      <div className={`h-1 w-1/3 rounded ${passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300'}`} />
                    </div>
                    <p className={`text-xs ${
                      passwordStrength === 'weak' ? 'text-red-500' :
                      passwordStrength === 'medium' ? 'text-yellow-600' :
                      passwordStrength === 'strong' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {t('auth.password_strength')}: {t(`auth.${passwordStrength}`) || t('common.info')}
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {t('auth.password_requirements')}
                </p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirm_password')} *</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t('auth.confirm_password')}
                    required
                    className={!validation.confirmPassword && formData.confirmPassword ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!validation.confirmPassword && formData.confirmPassword && (
                  <p className="text-xs text-red-500">{t('auth.passwords_no_match')}</p>
                )}
              </div>

              {/* Address (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="address">{t('auth.delivery_address')} ({t('auth.optional')})</Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={t('auth.delivery_address')}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.creating_account')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t('auth.create_account')}
                  </>
                )}
              </Button>

              {/* Login Link */}
              <div className="text-center text-sm">
                <span className="text-gray-600">{t('auth.already_have_account')} </span>
                <Link to="/my-account" className="text-orange-600 hover:text-orange-700 font-medium">
                  {t('auth.sign_in')}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Terms & Privacy */}
        <p className="text-center text-xs text-gray-500 mt-6">
          {t('auth.terms_agreement')} {' '}
          <Link to="/terms" className="text-orange-600 hover:underline">{t('auth.terms_service')}</Link>
          {' '} {t('auth.and')} {' '}
          <Link to="/privacy" className="text-orange-600 hover:underline">{t('auth.privacy_policy')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
